export type BuilderCostingPricingField =
	| 'mrp'
	| 'supplierDiscount'
	| 'landingPrice'
	| 'margin'
	| 'netPrice'
	| 'gst'
	| 'grossPrice'
	| 'basePrice';

export type BuilderCostingGrossPriceField = 'basePrice' | 'cost' | 'rate' | 'boxRate' | 'totalCost' | 'paintCost' | 'price';
export type BuilderCostingEditablePricingField = BuilderCostingPricingField | BuilderCostingGrossPriceField;

export const BUILDER_COSTING_PRICING_FIELDS = new Set<BuilderCostingPricingField>([
	'mrp',
	'supplierDiscount',
	'landingPrice',
	'margin',
	'netPrice',
	'gst',
	'grossPrice',
	'basePrice'
]);

export const isBuilderCostingPricingField = (field: string): field is BuilderCostingPricingField =>
	BUILDER_COSTING_PRICING_FIELDS.has(field as BuilderCostingPricingField);

export const isBuilderCostingEditablePricingField = (
	field: string,
	grossPriceField: BuilderCostingGrossPriceField = 'basePrice'
): field is BuilderCostingEditablePricingField => isBuilderCostingPricingField(field) || field === grossPriceField;

export const isBuilderCostingPercentField = (field: BuilderCostingPricingField) =>
	field === 'supplierDiscount' || field === 'margin' || field === 'gst';

export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export const parseBuilderCostingPriceValue = (value: unknown): number => {
	if (value == null || value === '') return 0;
	if (typeof value === 'number' && !Number.isNaN(value)) return value;
	const str = String(value).trim().replace(/,/g, '');
	if (str === '') return 0;
	const num = Number(str);
	return Number.isNaN(num) ? 0 : Math.max(0, num);
};
export const parseBuilderCostingPercentValue = (value: unknown): number => {
	if (value == null || value === '') return 0;
	const num = typeof value === 'number' ? value : Number(String(value).trim().replace(/,/g, ''));
	if (Number.isNaN(num)) return 0;
	return Math.max(0, Math.min(100, num));
};

export const computeBuilderCostingPricingFromField = (
	item: Record<string, any>,
	trigger: BuilderCostingEditablePricingField,
	grossPriceField: BuilderCostingGrossPriceField = 'basePrice'
): Record<string, any> => {
	const normalizedTrigger: BuilderCostingPricingField =
		trigger === grossPriceField && trigger !== 'basePrice' ? 'grossPrice' : (trigger as BuilderCostingPricingField);
	const normalizedItem =
		trigger === grossPriceField && trigger !== 'basePrice' ? { ...item, grossPrice: item[grossPriceField] } : item;

	const d = (normalizedItem.supplierDiscount ?? 0) / 100;
	const m = (normalizedItem.margin ?? 0) / 100;
	const g = (normalizedItem.gst ?? 0) / 100;

	let mrp: number | null = normalizedItem.mrp ?? null;
	let landing: number | null = normalizedItem.landingPrice ?? null;
	let net: number | null = normalizedItem.netPrice ?? null;
	let gross: number | null = normalizedItem.grossPrice ?? null;
	let base: number | null = normalizedItem.basePrice ?? null;

	switch (normalizedTrigger) {
		case 'mrp': {
			if (mrp == null) {
				return {
					...item,
					landingPrice: null,
					netPrice: null,
					grossPrice: null,
					basePrice: grossPriceField === 'basePrice' ? null : item.basePrice,
					[grossPriceField]: null
				};
			}
			landing = round2(mrp * (1 - d));
			net = round2(landing * (1 + m));
			gross = round2(net * (1 + g));
			if (grossPriceField === 'basePrice') base = gross;
			break;
		}
		case 'supplierDiscount': {
			if (mrp == null) return item;
			landing = round2(mrp * (1 - d));
			net = round2(landing * (1 + m));
			gross = round2(net * (1 + g));
			if (grossPriceField === 'basePrice') base = gross;
			break;
		}
		case 'landingPrice': {
			if (landing != null && 1 - d !== 0) mrp = round2(landing / (1 - d));
			net = round2((landing ?? 0) * (1 + m));
			gross = round2(net * (1 + g));
			if (grossPriceField === 'basePrice') base = gross;
			break;
		}
		case 'margin': {
			net = round2((landing ?? 0) * (1 + m));
			gross = round2(net * (1 + g));
			if (grossPriceField === 'basePrice') base = gross;
			break;
		}
		case 'netPrice': {
			if (net != null && 1 + m !== 0) landing = round2(net / (1 + m));
			if (landing != null && 1 - d !== 0) mrp = round2(landing / (1 - d));
			gross = round2((net ?? 0) * (1 + g));
			if (grossPriceField === 'basePrice') base = gross;
			break;
		}
		case 'gst': {
			gross = round2((net ?? 0) * (1 + g));
			if (grossPriceField === 'basePrice') base = gross;
			break;
		}
		case 'grossPrice': {
			if (gross != null && 1 + g !== 0) net = round2(gross / (1 + g));
			if (net != null && 1 + m !== 0) landing = round2(net / (1 + m));
			if (landing != null && 1 - d !== 0) mrp = round2(landing / (1 - d));
			if (grossPriceField === 'basePrice') base = gross;
			break;
		}
		case 'basePrice': {
			gross = base;
			if (gross != null && 1 + g !== 0) net = round2(gross / (1 + g));
			if (net != null && 1 + m !== 0) landing = round2(net / (1 + m));
			if (landing != null && 1 - d !== 0) mrp = round2(landing / (1 - d));
			break;
		}
		default:
			return item;
	}

	return {
		...item,
		mrp,
		landingPrice: landing,
		netPrice: net,
		grossPrice: gross,
		basePrice: base,
		[grossPriceField]: gross
	};
};

export const updateBuilderCostingPricingFormValue = (
	values: Record<string, any>,
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
	field: BuilderCostingEditablePricingField,
	value: unknown,
	grossPriceField: BuilderCostingGrossPriceField = 'basePrice'
) => {
	const parsed =
		isBuilderCostingPricingField(field) && isBuilderCostingPercentField(field)
			? parseBuilderCostingPercentValue(value)
			: parseBuilderCostingPriceValue(value);
	const nextValues = computeBuilderCostingPricingFromField({ ...values, [field]: parsed }, field, grossPriceField);

	Object.entries(nextValues).forEach(([key, nextValue]) => {
		if (key in values || key === field) {
			setFieldValue(key, nextValue, false);
		}
	});
};
